import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../services/api';

function InstanceDetail() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sqlAccessCidrs, setSqlAccessCidrs] = useState(null);
  const [sqlAccessUpdating, setSqlAccessUpdating] = useState(false);
  const [sqlAccessMessage, setSqlAccessMessage] = useState('');

  useEffect(() => {
    fetchInstance();
    const interval = setInterval(fetchInstance, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [instanceId]);

  const fetchInstance = async () => {
    try {
      const response = await apiClient.get(`/instances/${instanceId}`);
      setInstance(response.data);
      setSqlAccessCidrs((current) => current === null
        ? (response.data.sql_allowed_cidrs || []).join(', ')
        : current);
    } catch (error) {
      console.error('Error fetching instance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/instances/${instanceId}`);
      navigate('/instances');
    } catch (error) {
      console.error('Error deleting instance:', error);
      alert('Failed to delete instance: ' + error.message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleStopStart = async (action) => {
    const isStop = action === 'stop';
    const confirmed = window.confirm(
      `${isStop ? 'Stop' : 'Start'} instance ${instance.project_name || instance.instance_id}?\n\n` +
      (isStop
        ? 'This pauses the Virtuoso ECS task. Storage, backups, network resources, and Marketplace agreement remain active.'
        : 'This restarts the Virtuoso ECS task. It may take a few minutes before endpoints are healthy.')
    );
    if (!confirmed) {
      return;
    }

    setLifecycleAction(action);
    try {
      await apiClient.post(`/instances/${instanceId}/${action}`);
      await fetchInstance();
    } catch (error) {
      console.error(`Error ${action}ing instance:`, error);
      alert(`Failed to ${action} instance: ${error.message}`);
    } finally {
      setLifecycleAction('');
    }
  };

  const handleSqlAccessUpdate = async (event) => {
    event.preventDefault();
    const cidrs = String(sqlAccessCidrs || '')
      .split(',')
      .map((cidr) => cidr.trim())
      .filter(Boolean);
    const confirmed = window.confirm(
      cidrs.length
        ? `Update secure SQL access to: ${cidrs.join(', ')}?\n\nThe update normally completes within a few minutes.`
        : 'Disable public secure SQL access?\n\nContainer Shell Access remains available.'
    );
    if (!confirmed) return;

    setSqlAccessUpdating(true);
    setSqlAccessMessage('');
    try {
      const response = await apiClient.put(`/instances/${instanceId}/sql-access`, {
        sql_allowed_cidrs: cidrs
      });
      setSqlAccessCidrs((response.data.sql_allowed_cidrs || []).join(', '));
      setSqlAccessMessage(response.data.message || 'Secure SQL access update started.');
      await fetchInstance();
    } catch (error) {
      console.error('Error updating secure SQL access:', error);
      setSqlAccessMessage(`Failed to update secure SQL access: ${error.message}`);
    } finally {
      setSqlAccessUpdating(false);
    }
  };

  const fetchPassword = async () => {
    setLoadingPassword(true);
    try {
      const response = await apiClient.get(`/instances/${instanceId}/password`);
      setCredentials(response.data);
      setShowPassword(true);
    } catch (error) {
      console.error('Error fetching password:', error);
      alert('Failed to retrieve password: ' + error.message);
    } finally {
      setLoadingPassword(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const downloadTextFile = (filename, content) => {
    const blob = new Blob([content], { type: 'text/x-shellscript' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const encodeScript = (script) => {
    if (typeof window === 'undefined' || !window.btoa) {
      return '';
    }
    return window.btoa(unescape(encodeURIComponent(script)));
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      running: 'badge-success',
      provisioning: 'badge-info',
      stopped: 'badge-warning',
      failed: 'badge-danger',
      terminated: 'badge-danger',
      terminating: 'badge-warning'
    };
    return statusClasses[status] || 'badge-info';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div className="loading-spinner" style={{ borderTopColor: 'var(--primary-color)' }}></div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>Instance not found</h3>
          <p>The instance you're looking for doesn't exist or has been deleted.</p>
          <Link to="/instances" className="btn btn-primary">Back to Instances</Link>
        </div>
      </div>
    );
  }

  // Normalize hostname to lowercase for SQL/ODBC client compatibility
  const hostname = instance.nlb_dns_name ? instance.nlb_dns_name.toLowerCase() : null;
  const isStopped = instance.status === 'stopped';
  const isDecommissioning = instance.status === 'terminating' || instance.status === 'terminated' || isStopped;
  const normalizedHealthStatus = instance.health_status || 'unknown';
  const healthBadgeClass = isDecommissioning
    ? 'badge-info'
    : normalizedHealthStatus === 'healthy'
      ? 'badge-success'
      : normalizedHealthStatus === 'unhealthy'
        ? 'badge-danger'
        : 'badge-warning';
  const healthLabel = isDecommissioning ? 'N/A' : normalizedHealthStatus;
  const buyerShellRoleArn = instance.buyer_shell_role_arn || '';
  const buyerShellExternalId = instance.buyer_shell_external_id || '';
  const buyerShellRegion = instance.buyer_shell_region || instance.region || '';
  const buyerShellCluster = instance.buyer_shell_cluster || instance.ecs_cluster || '';
  const buyerShellService = instance.buyer_shell_service || instance.ecs_service || '';
  const buyerShellContainer = instance.buyer_shell_container || 'virtuoso';
  const sellerAccountMatch = buyerShellRoleArn.match(/^arn:aws:iam::([0-9]{12}):role\//);
  const sellerAccountId = sellerAccountMatch ? sellerAccountMatch[1] : '545314153384';
  const buyerShellRolePattern = `arn:aws:iam::${sellerAccountId}:role/virtuoso-*-buyer-shell`;
  const shellSessionName = `virtuoso-${String(instance.instance_id || 'instance').replace(/[^A-Za-z0-9+=,.@-]/g, '-')}`;
  const shellCredentialFile = `/tmp/${String(instance.instance_id || 'virtuoso')}-shell-creds.json`;
  const buyerIamPolicy = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "AssumeVirtuosoSaaSBuyerShellRoles",
      "Effect": "Allow",
      "Action": "sts:AssumeRole",
      "Resource": "${buyerShellRolePattern}"
    }
  ]
}`;
  const shellAccessScript = buyerShellRoleArn
    ? `#!/usr/bin/env bash
set -euo pipefail

ROLE_ARN="${buyerShellRoleArn}"
EXTERNAL_ID="${buyerShellExternalId}"
REGION="${buyerShellRegion}"
CLUSTER="${buyerShellCluster}"
SERVICE="${buyerShellService}"
CONTAINER="${buyerShellContainer}"
SESSION_NAME="${shellSessionName}"
CREDS_FILE="${shellCredentialFile}"

unset AWS_ACCESS_KEY_ID AWS_SECRET_ACCESS_KEY AWS_SESSION_TOKEN

echo "Checking current buyer AWS identity..."
aws sts get-caller-identity

echo "Assuming Virtuoso instance shell role..."
aws sts assume-role \\
  --role-arn "$ROLE_ARN" \\
  --role-session-name "$SESSION_NAME" \\
  --external-id "$EXTERNAL_ID" \\
  --query 'Credentials.[AccessKeyId,SecretAccessKey,SessionToken]' \\
  --output text > "$CREDS_FILE"

read AWS_ACCESS_KEY_ID AWS_SECRET_ACCESS_KEY AWS_SESSION_TOKEN < "$CREDS_FILE"
export AWS_ACCESS_KEY_ID AWS_SECRET_ACCESS_KEY AWS_SESSION_TOKEN

echo "Using scoped shell role:"
aws sts get-caller-identity

TASK_ARN=$(aws ecs list-tasks \\
  --region "$REGION" \\
  --cluster "$CLUSTER" \\
  --service-name "$SERVICE" \\
  --desired-status RUNNING \\
  --query 'taskArns[0]' \\
  --output text)

if [ -z "$TASK_ARN" ] || [ "$TASK_ARN" = "None" ]; then
  echo "No running ECS task found for service $SERVICE"
  exit 1
fi

echo "Opening shell on task $TASK_ARN"
aws ecs execute-command \\
  --region "$REGION" \\
  --cluster "$CLUSTER" \\
  --task "$TASK_ARN" \\
  --container "$CONTAINER" \\
  --interactive \\
  --command "/bin/bash"`
    : '';
  const shellScriptFilename = `virtuoso-shell-${instance.instance_id || 'instance'}.sh`;
  const downloadShellScript = () => downloadTextFile(shellScriptFilename, `${shellAccessScript}\n`);
  const dbPath = instance.virtuoso_db_path || (instance.virtuoso_edition === 'opensource' ? '/opt/virtuoso-opensource/database' : '/opt/virtuoso/database');
  const backupPrefix = `${dbPath}/backup/virtuoso_#`;
  const restorePrefix = 'virtuoso_#';
  const backupS3Uri = instance.backup_s3_uri || '';
  // ECS task-definition revisions are deregistered during a tenant refresh.
  // Use the family so a downloaded operational script selects the active revision.
  const taskDefinitionFamily = (arn) => (arn || '').replace(/:\d+$/, '');
  const backupSyncTaskDefinitionArn = taskDefinitionFamily(instance.backup_sync_task_definition_arn);
  const backupSyncCluster = instance.backup_sync_cluster || buyerShellCluster;
  const backupSyncRegion = instance.backup_sync_region || buyerShellRegion;
  const backupSyncSubnetIds = instance.backup_sync_subnet_ids || '';
  const backupSyncSecurityGroupId = instance.backup_sync_security_group_id || '';
  const backupRestoreSyncTaskDefinitionArn = taskDefinitionFamily(instance.backup_restore_sync_task_definition_arn);
  const backupRestoreRunnerTaskDefinitionArn = taskDefinitionFamily(instance.backup_restore_runner_task_definition_arn);
  const backupCommand = `set -euo pipefail
DB_PATH="${dbPath}"
BACKUP_DIR="$DB_PATH/backup"
BACKUP_PREFIX="virtuoso_#"
ISQL="\${VIRTUOSO_HOME:-}/bin/isql"
if [ ! -x "$ISQL" ]; then ISQL="$(command -v isql || command -v isql-v || true)"; fi
if [ -z "$ISQL" ] || [ ! -x "$ISQL" ]; then echo "Virtuoso isql binary not found"; exit 1; fi
if [ -z "\${DBA_PASSWORD:-}" ]; then echo "DBA_PASSWORD is not available in container environment"; exit 1; fi
mkdir -p "$BACKUP_DIR"
DB_OWNER="$(stat -c '%u:%g' "$DB_PATH" 2>/dev/null || true)"
if [ -n "$DB_OWNER" ]; then
  chown "$DB_OWNER" "$BACKUP_DIR" 2>/dev/null || true
fi
chmod 775 "$BACKUP_DIR" 2>/dev/null || true
if find "$BACKUP_DIR" -maxdepth 1 -type f -name 'virtuoso_*.bp' | grep -q .; then
  LEGACY_DIR="$BACKUP_DIR/legacy-$(date -u +%Y%m%d%H%M%S)"
  mkdir -p "$LEGACY_DIR"
  find "$BACKUP_DIR" -maxdepth 1 -type f -name 'virtuoso_*.bp' -exec mv {} "$LEGACY_DIR"/ \\;
  echo "Moved existing backup files to $LEGACY_DIR before starting a new backup chain"
fi
BACKUP_SQL="
delete from DB.DBA.SYS_BACKUP_DIRS;
insert into DB.DBA.SYS_BACKUP_DIRS (bd_id, bd_dir) values (1, '$BACKUP_DIR');
backup_online('$BACKUP_PREFIX', 10000, 0, vector ('$BACKUP_DIR'));
"
BACKUP_LOG="/tmp/virtuoso-online-backup.out"
printf "%s\n" "$BACKUP_SQL" | "$ISQL" 1112 dba "$DBA_PASSWORD" -E 2>&1 | tee "$BACKUP_LOG"
if grep -qE '^\\*\\*\\* Error|^Error ' "$BACKUP_LOG"; then
  echo "Virtuoso online backup failed"
  exit 1
fi
echo "Backup request submitted with prefix: $BACKUP_PREFIX in $BACKUP_DIR"
ls -lh "$BACKUP_DIR" | tail -20`;
  const backupCommandB64 = encodeScript(backupCommand);
  const backupExecCommand = backupCommandB64
    ? `/bin/bash -lc 'echo ${backupCommandB64} | base64 -d > /tmp/virtuoso-online-backup.sh && chmod +x /tmp/virtuoso-online-backup.sh && /tmp/virtuoso-online-backup.sh'`
    : '';
  const backupSyncScript = backupSyncTaskDefinitionArn && backupSyncSubnetIds && backupSyncSecurityGroupId
    ? `
echo "Starting durable S3 backup sync task..."
BACKUP_SYNC_TASK_ARN=$(aws ecs run-task \
  --region "${backupSyncRegion}" \
  --cluster "${backupSyncCluster}" \
  --launch-type FARGATE \
  --task-definition "${backupSyncTaskDefinitionArn}" \
  --network-configuration "awsvpcConfiguration={subnets=[${backupSyncSubnetIds}],securityGroups=[${backupSyncSecurityGroupId}],assignPublicIp=DISABLED}" \
  --query 'tasks[0].taskArn' \
  --output text)

if [ -z "$BACKUP_SYNC_TASK_ARN" ] || [ "$BACKUP_SYNC_TASK_ARN" = "None" ]; then
  echo "Failed to start backup sync task"
  exit 1
fi

echo "Waiting for backup sync task: $BACKUP_SYNC_TASK_ARN"
aws ecs wait tasks-stopped \
  --region "${backupSyncRegion}" \
  --cluster "${backupSyncCluster}" \
  --tasks "$BACKUP_SYNC_TASK_ARN"

BACKUP_SYNC_EXIT_CODE=$(aws ecs describe-tasks \
  --region "${backupSyncRegion}" \
  --cluster "${backupSyncCluster}" \
  --tasks "$BACKUP_SYNC_TASK_ARN" \
  --query 'tasks[0].containers[0].exitCode' \
  --output text)

if [ "$BACKUP_SYNC_EXIT_CODE" != "0" ]; then
  echo "Backup sync task failed with exit code: $BACKUP_SYNC_EXIT_CODE"
  exit 1
fi

echo "Backup synced to: ${backupS3Uri}"
`
    : '';
  const shellBackupScript = buyerShellRoleArn && backupSyncScript && backupExecCommand
    ? `${shellAccessScript.replace('--command "/bin/bash"', `--command ${JSON.stringify(backupExecCommand)}`)}${backupSyncScript}`
    : '';
  const backupScriptFilename = `virtuoso-online-backup-${instance.instance_id || 'instance'}.sh`;
  const downloadBackupScript = () => downloadTextFile(backupScriptFilename, `${shellBackupScript}
`);
  const restoreGuidance = backupS3Uri && backupRestoreSyncTaskDefinitionArn && backupRestoreRunnerTaskDefinitionArn
    ? `# Seller-admin controlled restore for ${instance.instance_id}
# This is not an online operation. Confirm the target backup set before running.
set -euo pipefail

REGION="${backupSyncRegion}"
CLUSTER="${backupSyncCluster}"
SERVICE="${buyerShellService}"
SUBNETS="${backupSyncSubnetIds}"
SECURITY_GROUP="${backupSyncSecurityGroupId}"
BACKUP_S3_URI="${backupS3Uri}"
RESTORE_SYNC_TASK_DEFINITION="${backupRestoreSyncTaskDefinitionArn}"
RESTORE_RUNNER_TASK_DEFINITION="${backupRestoreRunnerTaskDefinitionArn}"

echo "Available durable S3 backup files:"
aws s3 ls "$BACKUP_S3_URI" --recursive --human-readable --summarize

echo "Stopping Virtuoso service before restore..."
aws ecs update-service --region "$REGION" --cluster "$CLUSTER" --service "$SERVICE" --desired-count 0
aws ecs wait services-stable --region "$REGION" --cluster "$CLUSTER" --services "$SERVICE"

echo "Syncing selected S3 backup set back to the tenant EFS backup directory..."
RESTORE_SYNC_TASK_ARN=$(aws ecs run-task \\
  --region "$REGION" \\
  --cluster "$CLUSTER" \\
  --launch-type FARGATE \\
  --task-definition "$RESTORE_SYNC_TASK_DEFINITION" \\
  --network-configuration "awsvpcConfiguration={subnets=[$SUBNETS],securityGroups=[$SECURITY_GROUP],assignPublicIp=DISABLED}" \\
  --query 'tasks[0].taskArn' \\
  --output text)
aws ecs wait tasks-stopped --region "$REGION" --cluster "$CLUSTER" --tasks "$RESTORE_SYNC_TASK_ARN"
RESTORE_SYNC_EXIT_CODE=$(aws ecs describe-tasks --region "$REGION" --cluster "$CLUSTER" --tasks "$RESTORE_SYNC_TASK_ARN" --query 'tasks[0].containers[0].exitCode' --output text)
if [ "$RESTORE_SYNC_EXIT_CODE" != "0" ]; then
  echo "Restore sync failed with exit code: $RESTORE_SYNC_EXIT_CODE. Leaving the service stopped."
  exit 1
fi

echo "Running Virtuoso restore task using +restore-backup ${restorePrefix}"
RESTORE_RUNNER_TASK_ARN=$(aws ecs run-task \\
  --region "$REGION" \\
  --cluster "$CLUSTER" \\
  --launch-type FARGATE \\
  --task-definition "$RESTORE_RUNNER_TASK_DEFINITION" \\
  --network-configuration "awsvpcConfiguration={subnets=[$SUBNETS],securityGroups=[$SECURITY_GROUP],assignPublicIp=DISABLED}" \\
  --query 'tasks[0].taskArn' \\
  --output text)
aws ecs wait tasks-stopped --region "$REGION" --cluster "$CLUSTER" --tasks "$RESTORE_RUNNER_TASK_ARN"
RESTORE_RUNNER_EXIT_CODE=$(aws ecs describe-tasks --region "$REGION" --cluster "$CLUSTER" --tasks "$RESTORE_RUNNER_TASK_ARN" --query 'tasks[0].containers[0].exitCode' --output text)
if [ "$RESTORE_RUNNER_EXIT_CODE" != "0" ]; then
  echo "Virtuoso restore failed with exit code: $RESTORE_RUNNER_EXIT_CODE. Leaving the service stopped."
  exit 1
fi

echo "Restarting Virtuoso service..."
aws ecs update-service --region "$REGION" --cluster "$CLUSTER" --service "$SERVICE" --desired-count 1
aws ecs wait services-stable --region "$REGION" --cluster "$CLUSTER" --services "$SERVICE"

echo "Validate Conductor, SPARQL, SQL listener, and application data before closing the restore ticket."`
    : `Online backups are created on the mounted database volume using prefix:
${backupPrefix}

Durable copies are synced to S3:
${backupS3Uri || 'Not configured for this instance'}

Restore is intentionally not run automatically from the buyer portal because Virtuoso restore requires the server to be stopped and current database files to be moved aside before starting Virtuoso with +restore-backup.

This instance does not yet expose restore task metadata. Backfill/reapply the tenant stack with the latest backup template, then use the Seller Admin Portal restore script.`;

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/instances" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '14px' }}>
            ← Back to Instances
          </Link>
          <h1 style={{ marginTop: '8px' }}>{instance.project_name || instance.instance_id}</h1>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {instance.status === 'running' && hostname && (
            <a
              href={`https://${hostname}/conductor`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Open Conductor
            </a>
          )}
          {instance.status === 'running' && (
            <button
              onClick={() => handleStopStart('stop')}
              className="btn btn-secondary"
              disabled={Boolean(lifecycleAction)}
            >
              {lifecycleAction === 'stop' ? 'Stopping...' : 'Stop Instance'}
            </button>
          )}
          {instance.status === 'stopped' && (
            <button
              onClick={() => handleStopStart('start')}
              className="btn btn-primary"
              disabled={Boolean(lifecycleAction)}
            >
              {lifecycleAction === 'start' ? 'Starting...' : 'Start Instance'}
            </button>
          )}
          {instance.status !== 'terminated' && instance.status !== 'terminating' && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn btn-danger"
            >
              Delete Instance
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {instance.status === 'provisioning' && (
        <div className="card" style={{ background: 'rgba(102, 126, 234, 0.1)', borderLeft: '4px solid var(--primary-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="loading-spinner" style={{ width: '24px', height: '24px', marginRight: '16px', borderTopColor: 'var(--primary-color)' }}></div>
            <div>
              <strong>Provisioning in progress</strong>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                Your instance is being created. This typically takes 5-10 minutes.
              </p>
            </div>
          </div>
        </div>
      )}

      {instance.status === 'failed' && (
        <div className="card" style={{ background: 'rgba(245, 101, 101, 0.1)', borderLeft: '4px solid var(--danger-color)' }}>
          <strong style={{ color: 'var(--danger-color)' }}>Provisioning failed</strong>
          <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
            There was an error creating your instance. Please try again or contact support.
          </p>
        </div>
      )}

      <div className="stats-grid">
        {/* General Information */}
        <div className="card">
          <div className="card-header">
            <h2>General Information</h2>
            <span className={`badge ${getStatusBadge(instance.status)}`}>{instance.status}</span>
          </div>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Instance ID</td>
                <td style={{ padding: '12px 0', fontFamily: 'monospace' }}>{instance.instance_id}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Project Name</td>
                <td style={{ padding: '12px 0' }}>{instance.project_name}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Tier</td>
                <td style={{ padding: '12px 0', textTransform: 'capitalize' }}>{instance.tier}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Region</td>
                <td style={{ padding: '12px 0' }}>{instance.region}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Edition</td>
                <td style={{ padding: '12px 0' }}>
                  {instance.virtuoso_edition === 'commercial' ? (
                    <span>
                      Commercial{' '}
                      <span style={{
                        background: 'var(--primary-color)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        marginLeft: '4px'
                      }}>Enterprise</span>
                    </span>
                  ) : (
                    <span>
                      Open Source{' '}
                      <span style={{
                        background: 'var(--success-color)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        marginLeft: '4px'
                      }}>Free</span>
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Created</td>
                <td style={{ padding: '12px 0' }}>{new Date(instance.created_at).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Connection Information */}
        <div className="card">
          <div className="card-header">
            <h2>Connection Information</h2>
          </div>
          {hostname ? (
            <table className="connection-details-table">
              <tbody>
                <tr>
                  <td className="connection-details-label">Host</td>
                  <td className="connection-details-value connection-details-monospace">{hostname}</td>
                </tr>
                <tr>
                  <td className="connection-details-label">HTTPS Conductor</td>
                  <td className="connection-details-value">
                    <a href={`https://${hostname}/conductor`} target="_blank" rel="noopener noreferrer">
                      https://{hostname}/conductor
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className="connection-details-label">HTTPS SPARQL</td>
                  <td className="connection-details-value">
                    <a href={`https://${hostname}/sparql`} target="_blank" rel="noopener noreferrer">
                      https://{hostname}/sparql
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className="connection-details-label">Secure SQL/ODBC/JDBC (TLS)</td>
                  <td className="connection-details-value connection-details-monospace">
                    {instance.virtuoso_sql_endpoint || `${hostname}:1112`}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p style={{ color: 'var(--text-muted)', padding: '20px 0' }}>
              Connection information will be available once provisioning is complete.
            </p>
          )}
        </div>

        {/* Credentials */}
        {hostname && (
          <div className="card">
            <div className="card-header">
              <h2>Database Credentials</h2>
            </div>
            {!credentials ? (
              <div style={{ padding: '20px 0' }}>
                <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
                  Click the button below to reveal your database credentials.
                </p>
                <button
                  onClick={fetchPassword}
                  className="btn btn-secondary"
                  disabled={loadingPassword}
                >
                  {loadingPassword ? 'Loading...' : 'Reveal Credentials'}
                </button>
              </div>
            ) : (
              <table style={{ width: '100%' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Username</td>
                    <td style={{ padding: '12px 0', fontFamily: 'monospace' }}>
                      {credentials.username}
                      <button
                        onClick={() => copyToClipboard(credentials.username)}
                        style={{ marginLeft: '12px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Password</td>
                    <td style={{ padding: '12px 0', fontFamily: 'monospace' }}>
                      {showPassword ? credentials.password : '••••••••••••••••'}
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ marginLeft: '12px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={() => copyToClipboard(credentials.password)}
                        style={{ marginLeft: '8px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Health & Infrastructure */}
      <div className="stats-grid">
        <div className="card">
          <div className="card-header">
            <h2>Health Status</h2>
          </div>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Health</td>
                <td style={{ padding: '12px 0' }}>
                  <span className={`badge ${healthBadgeClass}`}>
                    {healthLabel}
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Last Health Check</td>
                <td style={{ padding: '12px 0' }}>
                  {isDecommissioning
                    ? 'N/A'
                    : instance.last_health_check
                      ? new Date(instance.last_health_check).toLocaleString()
                      : 'Never'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Infrastructure</h2>
          </div>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>VPC ID</td>
                <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px' }}>{instance.vpc_id || '-'}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>ECS Cluster</td>
                <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px' }}>{instance.ecs_cluster_arn || '-'}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>EFS ID</td>
                <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px' }}>{instance.efs_file_system_id || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Secure SQL Access</h2>
          </div>
          {instance.status === 'running' ? (
            <form onSubmit={handleSqlAccessUpdate}>
              <label htmlFor="sqlAccessCidrs">Allowed IPv4 CIDRs</label>
              <input
                type="text"
                id="sqlAccessCidrs"
                value={sqlAccessCidrs === null ? '' : sqlAccessCidrs}
                onChange={(event) => setSqlAccessCidrs(event.target.value)}
                placeholder="203.0.113.45/32, 198.51.100.0/24"
                style={{ width: '100%', marginTop: '8px' }}
                disabled={sqlAccessUpdating}
              />
              <p className="help-text">
                TLS SQL is exposed on port 1112 only to these trusted ranges. Leave blank to disable public SQL access. `0.0.0.0/0` is not allowed.
              </p>
              {instance.sql_access_update_status === 'updating' && (
                <p style={{ color: 'var(--warning-color)', fontSize: '14px' }}>Applying secure SQL access update...</p>
              )}
              {instance.sql_access_update_status === 'failed' && instance.sql_access_update_error && (
                <p style={{ color: 'var(--danger-color)', fontSize: '14px' }}>{instance.sql_access_update_error}</p>
              )}
              {sqlAccessMessage && (
                <p style={{ color: sqlAccessMessage.startsWith('Failed') ? 'var(--danger-color)' : 'var(--success-color)', fontSize: '14px' }}>{sqlAccessMessage}</p>
              )}
              <button type="submit" className="btn btn-secondary" disabled={sqlAccessUpdating}>
                {sqlAccessUpdating ? 'Updating...' : 'Update Secure SQL Access'}
              </button>
            </form>
          ) : (
            <p style={{ color: 'var(--text-muted)' }}>Start the instance before changing its secure SQL access ranges.</p>
          )}
        </div>
      </div>

      {/* Shell Access */}
      <div className="card">
        <div className="card-header">
          <h2>Container Shell Access</h2>
        </div>
        {buyerShellRoleArn && !isDecommissioning ? (
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
              Use a buyer AWS IAM user or role to open an ECS Exec shell into this Virtuoso container. AWS root credentials cannot assume shell access roles.
            </p>

            <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(72, 187, 120, 0.08)', border: '1px solid rgba(72, 187, 120, 0.25)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <strong>One-time buyer IAM setup</strong>
                <button onClick={() => copyToClipboard(buyerIamPolicy)} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>Copy IAM Policy</button>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '12px' }}>
                Attach this policy once to the buyer IAM user or role used for shell access. It covers all current and future Virtuoso SaaS shell roles in the seller account; each seller role is still restricted to its own tenant instance and External ID.
              </p>
              <pre className="command-block command-block-light">{buyerIamPolicy}</pre>
            </div>

            <table style={{ width: '100%', marginBottom: '20px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Role ARN</td>
                  <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>{buyerShellRoleArn}</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>External ID</td>
                  <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px' }}>{buyerShellExternalId}</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>ECS Target</td>
                  <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-word' }}>
                    {buyerShellRegion} / {buyerShellCluster} / {buyerShellService} / {buyerShellContainer}
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <strong>Connect to this Virtuoso container</strong>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => copyToClipboard(shellAccessScript)} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>Copy Script</button>
                  <button onClick={downloadShellScript} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>Download Script</button>
                </div>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '12px' }}>
                Run this from a terminal configured with the buyer IAM user or role credentials. Requires AWS CLI v2 and the Session Manager plugin.
              </p>
              <pre className="command-block">{shellAccessScript}</pre>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', padding: '20px 0' }}>
            Shell access details will be available after this instance is provisioned with buyer shell access enabled.
          </p>
        )}
      </div>

      {/* Backup & Recovery */}
      <div className="card">
        <div className="card-header">
          <h2>Backup & Recovery</h2>
        </div>
        {buyerShellRoleArn && shellBackupScript && !isDecommissioning ? (
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
              Use Virtuoso online backup to configure the standard backup directory and create the first full backup.
              Backups are created on the instance database volume, then synced to durable S3 storage. Daily incrementals are handled by the per-instance EventBridge/Fargate backup workflow.
            </p>
            <table style={{ width: '100%', marginBottom: '20px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Backup Prefix</td>
                  <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>{backupPrefix}</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Schedule</td>
                  <td style={{ padding: '12px 0' }}>EventBridge runs a per-instance Fargate workflow every 24 hours: backup first, S3 sync second.</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--text-muted)' }}>Durable S3 Copy</td>
                  <td style={{ padding: '12px 0', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>{backupS3Uri || 'Not configured for this instance'}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <strong>Run online backup</strong>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => copyToClipboard(shellBackupScript)} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>Copy Script</button>
                  <button onClick={downloadBackupScript} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>Download Script</button>
                </div>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '12px' }}>
                The script uses ECS Exec to configure <code>SYS_BACKUP_DIRS</code> and run the initial <code>DB.DBA.BACKUP_MAKE()</code>, then starts a scoped Fargate sync task to copy backup files to S3.
              </p>
              <pre className="command-block">{shellBackupScript}</pre>
            </div>

            <div style={{ padding: '16px', background: 'rgba(237, 137, 54, 0.08)', border: '1px solid rgba(237, 137, 54, 0.25)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <strong>Restore guidance</strong>
                <button onClick={() => copyToClipboard(restoreGuidance)} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>Copy Guidance</button>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>
                Restore is a controlled operation because Virtuoso must be stopped before restore. Coordinate with OpenLink support or a seller admin before replacing database files.
              </p>
              <pre className="command-block command-block-light" style={{ marginTop: '12px' }}>{restoreGuidance}</pre>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', padding: '20px 0' }}>
            Backup commands will be available after this instance is provisioned with shell access enabled.
          </p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '400px', margin: '20px' }}>
            <h2 style={{ color: 'var(--danger-color)', marginBottom: '16px' }}>Delete Instance?</h2>
            <p style={{ marginBottom: '24px' }}>
              Are you sure you want to delete <strong>{instance.project_name}</strong>?
              This action cannot be undone and all data will be lost.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleDelete} className="btn btn-danger" disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Instance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstanceDetail;
